/**
 * The tasks a sequence is built from: what's left to do, in the given order.
 *
 * Completed work is skipped — chaining onto something already done adds a
 * constraint that is satisfied the moment it exists. Habits are skipped
 * because they recur; "wait for the gym" has no end.
 */
export function sequenceable(ordered) {
    return ordered.filter((t) => t.status !== 'completed' && !t.is_habit && !t.is_archived);
}
/** Does `from` depend on `to`, directly or through anything else? */
export function dependsOn(from, to, dependenciesOf) {
    const seen = new Set();
    const stack = [from];
    while (stack.length > 0) {
        const id = stack.pop();
        if (id === to)
            return true;
        if (seen.has(id))
            continue;
        seen.add(id);
        stack.push(...dependenciesOf(id));
    }
    return false;
}
function resolver(allTasks, proposed) {
    const byId = new Map(allTasks.map((t) => [t.id, t]));
    return (id) => proposed.get(id) ?? byId.get(id)?.dependencies ?? [];
}
/**
 * Make each task in `ordered` wait for the one before it.
 *
 * Existing dependencies are kept — this adds a link, it doesn't replace what
 * you already set up. A link that already exists produces no change, so
 * running it twice is a no-op rather than a duplicate.
 */
export function sequenceLinks(ordered, allTasks) {
    const chain = sequenceable(ordered);
    const proposed = new Map();
    const dependenciesOf = resolver(allTasks, proposed);
    const skipped = [];
    for (let i = 1; i < chain.length; i++) {
        const previous = chain[i - 1];
        const current = chain[i];
        const deps = [...dependenciesOf(current.id)];
        if (deps.includes(previous.id))
            continue;
        // Adding "current waits for previous" closes a loop precisely when
        // previous already waits for current.
        if (dependsOn(previous.id, current.id, dependenciesOf)) {
            skipped.push(current.id);
            continue;
        }
        deps.push(previous.id);
        proposed.set(current.id, deps);
    }
    return {
        changes: [...proposed.entries()].map(([id, dependencies]) => ({ id, dependencies })),
        skipped,
    };
}
/**
 * Undo the chain: drop each task's dependency on the one before it, and leave
 * everything else it depends on alone.
 *
 * Keyed off the same order rather than off a stored flag, so it's exactly the
 * inverse of the link above and there's no extra state to get out of step.
 */
export function unsequenceLinks(ordered, allTasks) {
    const chain = sequenceable(ordered);
    const byId = new Map(allTasks.map((t) => [t.id, t]));
    const changes = [];
    for (let i = 1; i < chain.length; i++) {
        const previous = chain[i - 1];
        const current = byId.get(chain[i].id) ?? chain[i];
        const deps = current.dependencies ?? [];
        if (!deps.includes(previous.id))
            continue;
        changes.push({ id: current.id, dependencies: deps.filter((d) => d !== previous.id) });
    }
    return { changes, skipped: [] };
}
/**
 * Is this list already a chain? Drives which of Link / Unlink to offer, and
 * saves showing "Link as a sequence" on something already linked.
 */
export function isSequenced(ordered) {
    const chain = sequenceable(ordered);
    if (chain.length < 2)
        return false;
    for (let i = 1; i < chain.length; i++) {
        if (!(chain[i].dependencies ?? []).includes(chain[i - 1].id))
            return false;
    }
    return true;
}
/**
 * Chain a freshly-created run of tasks, in creation order.
 *
 * Used by bulk capture, where the ids only exist after the tasks do. Same
 * rules as above; `allTasks` is the state right after creation.
 */
export function sequenceCreated(createdIds, allTasks) {
    const byId = new Map(allTasks.map((t) => [t.id, t]));
    const ordered = createdIds.map((id) => byId.get(id)).filter((t) => t !== undefined);
    return sequenceLinks(ordered, allTasks);
}
//# sourceMappingURL=sequence.js.map